/**
 * Settings › Roles and permissions (docs/rbac.md section 8): every person and agent gets exactly the access their role
 * needs. A matrix of roles × the seven verbs (a tick on every area, a dash on some), who holds what, and how access
 * works with the badges true for this deployment.
 *
 * While roles and permissions are off (AIOE_RBAC), the page shows today's roles read-only, each with what it may do now
 * as the API computes it. With them on, an administrator makes and changes custom roles (a sheet of kinds × verbs, never
 * beyond their own), and gives a role to a person, a group or an agent at the organisation, a team or a project. Built-in
 * roles are read-only. Narrow, each role is a card that names its verbs in words.
 */

import {
  AREA_KEYS,
  AREA_TEXT,
  KIND_TEXT,
  NEVER_HELD,
  PRINCIPAL_TYPE_TEXT,
  RESOURCE_AREAS,
  RESOURCE_KINDS,
  VERBS,
  VERB_TEXT,
  compactGrants,
  grantTable,
  verbReach,
  type AreaKey,
  type Grant,
  type HeldAction,
  type PrincipalType,
  type RbacOverview,
  type ResourceKind,
  type RoleAssignment,
  type RoleDefinition,
  type RoleScope,
  type Verb,
  type VerbReach,
} from '@aioe/shared';
import { Check, FileText, Fingerprint, HandOk, ListCheck, Persons, Plus, Shield, ShieldCheck } from '@gravity-ui/icons';
import { Checkbox } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { type ReactElement, useMemo, useState } from 'react';

import { ApiError, problemText, rbacRoutes, SIGN_IN_AGAIN } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { groupsAvailable } from '../directory.ts';
import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { listCount, NoMatch, ShowMore, useListSearch } from '../pagedList.tsx';
import { SearchBox } from './ui/SearchBox.tsx';
import { Button, ChoiceSelect, ConfirmDialog, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, Stat } from './ui.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import './rolesPermissions.css';

const TONE = VIEW_TONES.settings!;

/* ---------- words ---------- */

const SOURCE_TEXT: Record<RoleDefinition['source'], string> = { 'built-in': 'Built-in', template: 'Custom', custom: 'Custom', today: 'Today’s role' };
const HOLDER_SOURCE: Record<RoleAssignment['source'], string> = {
  assigned: 'Given here',
  'app-role': 'From the directory’s app role',
  'cloud-role': 'From their membership',
  project: 'From the project',
  'team-owner': 'The team’s owner',
  'area-manager': 'A business area’s manager',
  default: 'Everyone signed in',
};

const scopeWords = (s: RoleScope): string => (s.type === 'organisation' ? 'Whole organisation' : `${s.type === 'team' ? 'Team' : 'Project'} ${s.name ?? s.id}`);
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
const listWords = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/** A verb's reach as a mark and a word: a filled tick on everything, an outlined tick on some areas, nothing on none. */
function VerbMark({ reach, verb }: { reach: VerbReach; verb: Verb }) {
  const word = reach === 'all' ? `${VERB_TEXT[verb].label}: everywhere` : reach === 'some' ? `${VERB_TEXT[verb].label}: some areas` : `${VERB_TEXT[verb].label}: no`;
  return (
    <span className="verb-mark" data-reach={reach} role="img" aria-label={word} title={word}>
      {reach === 'none' ? null : <Check aria-hidden="true" />}
    </span>
  );
}

/** "View, Read and Act everywhere; Create on some areas": a role's verbs in words, for the narrow cards. */
function verbsInWords(grants: readonly string[]): string {
  const r = verbReach(grants);
  const all = VERBS.filter((v) => r[v] === 'all').map((v) => VERB_TEXT[v].label);
  const some = VERBS.filter((v) => r[v] === 'some').map((v) => VERB_TEXT[v].label);
  if (!all.length && !some.length) return 'Nothing.';
  return [all.length ? `${listWords(all)} everywhere` : '', some.length ? `${listWords(some)} on some areas` : ''].filter(Boolean).join('; ') + '.';
}

/* ---------- the panel ---------- */

export function RolesPermissionsPanel() {
  const view = useLoad(() => rbacRoutes.overview(), []);
  const [open, setOpen] = useState<RoleDefinition | 'new'>();
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState<RoleAssignment>();
  const [error, setError] = useState<string>();
  const step = useStepUp();
  const v = view.data;
  if (view.error) return <ErrorNote error={view.error} />;
  if (!v) return null;
  const roleName = (id: string) => v.roles.find((r) => r.id === id)?.name ?? id;
  const builtIn = v.roles.filter((r) => r.source === 'built-in').length;
  const custom = v.roles.filter((r) => r.source === 'custom' || r.source === 'template').length;
  const atLimit = v.customRoleLimit !== null && v.roles.filter((r) => r.source === 'custom').length >= v.customRoleLimit;
  const remove = async (a: RoleAssignment) => {
    setError(undefined);
    try {
      await step(() => rbacRoutes.unassign(a.id), 'Sign in again to take this role away.');
      setRemoving(undefined);
      view.reload();
    } catch (e) {
      setRemoving(undefined);
      setError(problemText(e));
    }
  };

  return (
    <>
      <div className="art-stats">
        <Stat tone={TONE} icon={<ShieldCheck />} label="Roles" value={v.roles.length} detail={v.mode === 'today' ? 'As they are today' : `${builtIn} built-in, ${custom} custom`} />
        <Stat tone={TONE} icon={<Persons />} label="People" value={v.counts.people} detail={plural(v.counts.agents, 'agent')} />
        <Stat tone={TONE} icon={<Shield />} label="Platform administrators" value={v.counts.platformAdministrators} detail={v.edition === 'cloud' ? 'Owners and admins' : 'Seen signing in'} />
        {v.mode === 'roles' ? <Stat tone={TONE} icon={<HandOk />} label="Waiting for a person" value={v.counts.waiting} detail="Agents’ actions held for approval" /> : null}
      </div>

      {v.mode === 'roles' ? <HeldActions waiting={v.counts.waiting} onDecided={() => view.reload()} /> : null}

      <ListGroup
        id="roles"
        title="Roles"
        count={v.roles.length}
        description="What each role may do, worked out by the platform. Open a role to see it area by area."
        actions={
          v.editable ? (
            <Button size="sm" variant="secondary" isDisabled={atLimit} onPress={() => setOpen('new')}>
              <Plus className="size-4" />
              New role
            </Button>
          ) : undefined
        }
      >
        {v.readOnlyReason ? <ListBlock className="text-xs text-muted">{v.readOnlyReason}</ListBlock> : null}
        <ListBlock flush className="roles-matrix-wrap">
          <div className="data-table-scroll">
            <table className="data-table roles-matrix">
              <thead>
                <tr>
                  <th>Role</th>
                  {VERBS.map((verb) => (
                    <th key={verb} className="roles-matrix__verb">
                      {VERB_TEXT[verb].label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {v.roles.map((role) => {
                  const reach = verbReach(role.grants);
                  return (
                    <tr key={role.id} data-pressable onClick={() => setOpen(role)}>
                      <td>
                        <button type="button" className="data-table__open font-medium" onClick={(e) => (e.stopPropagation(), setOpen(role))}>
                          {role.name}
                        </button>
                        <span className="data-table__meta">{role.scopes.includes('organisation') ? SOURCE_TEXT[role.source] : `${SOURCE_TEXT[role.source]}, for one ${role.scopes[0]}`}</span>
                      </td>
                      {VERBS.map((verb) => (
                        <td key={verb} className="roles-matrix__verb">
                          <VerbMark reach={reach[verb]} verb={verb} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ListBlock>
        <div className="roles-cards">
          {v.roles.map((role) => (
            <ListRow key={role.id} title={role.name} description={`${SOURCE_TEXT[role.source]}. ${verbsInWords(role.grants)}`} onPress={() => setOpen(role)} />
          ))}
        </div>
        <ListBlock className="text-xs text-muted">Applied across agents, teams, workflows, roles, economics and audit. A filled tick holds the verb on everything; an outlined one on some areas or only their own.</ListBlock>
      </ListGroup>

      <Holders view={v} roleName={roleName} onAssign={v.editable ? () => setAssigning(true) : undefined} onRemove={v.editable ? setRemoving : undefined} />
      <ErrorNote error={error} />

      <Principles view={v} />

      {open ? <RoleSheet view={v} role={open === 'new' ? undefined : open} onClose={() => setOpen(undefined)} onSaved={() => (setOpen(undefined), view.reload())} /> : null}
      {assigning ? <AssignSheet view={v} onClose={() => setAssigning(false)} onSaved={() => (setAssigning(false), view.reload())} /> : null}
      <ConfirmDialog
        isOpen={removing !== undefined}
        title={`Take ${removing ? roleName(removing.roleId) : 'this role'} away?`}
        confirmLabel="Take away"
        danger
        onConfirm={() => removing && remove(removing)}
        onCancel={() => setRemoving(undefined)}
      >
        {removing ? `${removing.principal.name ?? removing.principal.id} no longer holds it at ${scopeWords(removing.scope).toLowerCase()}. It is recorded in the audit trail.` : null}
      </ConfirmDialog>
    </>
  );
}

/** A change that may need a recent sign-in: asks for one and tries once more. */
export function useStepUp() {
  const { signInAgain } = useAuth();
  return async <T,>(call: () => Promise<T>, fallback: string): Promise<T> => {
    try {
      return await call();
    } catch (e) {
      if (!(e instanceof ApiError && e.code === SIGN_IN_AGAIN) || !signInAgain) throw e;
      const message = typeof (e.body as { message?: unknown })?.message === 'string' ? (e.body as { message: string }).message : fallback;
      await signInAgain(message);
      return call();
    }
  };
}

/* ---------- agents' actions held for a person (step 6) ---------- */

/**
 * `quiet`: on another page (Activity), the group shows only while something waits and says nothing when the list cannot
 * be read. The API lists a Team Administrator the held actions of their teams' agents, and the whole organisation's to
 * whoever reads Roles. `waiting`: the count the Roles screen already holds.
 */
export function HeldActions({ onDecided, quiet, waiting = 0 }: { onDecided: () => void; quiet?: boolean; waiting?: number }) {
  const list = useLoad(() => rbacRoutes.held(), [], quiet ? 30_000 : undefined);
  const step = useStepUp();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const decide = async (h: HeldAction, decision: 'approve' | 'reject') => {
    setError(undefined);
    setBusy(h.id);
    try {
      await step(() => rbacRoutes.decideHeld(h.id, { decision }), decision === 'approve' ? 'Sign in again to approve this.' : 'Sign in again to reject this.');
      list.reload();
      onDecided();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const actions = list.data?.actions ?? [];
  const refused = list.data?.refused ?? [];
  // On another page (quiet): only while something waits or was refused after an approval, and silent when unreadable.
  if (quiet && (list.error || (actions.length === 0 && refused.length === 0))) return null;
  // Nothing waits and nothing was refused after an approval: the section stays out of the way.
  if (!quiet && waiting === 0 && actions.length === 0 && refused.length === 0) return null;
  return (
    <>
    {actions.length > 0 || waiting > 0 ? (
    <ListGroup id="held" title="Waiting for a person" count={actions.length} description="An agent’s high-impact action does not run until a person approves it. Approve lets it run once; reject ends it. The Inbox asks the same.">
      {actions.length === 0 ? <ListBlock className="text-sm text-muted">Nothing waits for a person.</ListBlock> : null}
      {actions.map((h) => (
        <ListRow
          key={h.id}
          title={h.agent.name ?? 'An agent'}
          description={
            <>
              {`It ${h.words}. `}
              {h.person ? (
                <>
                  For <SubjectName id={h.person.id} fallback={h.person.name ?? 'a person'} />
                </>
              ) : h.accountable ? (
                <>
                  On a trigger; <SubjectName id={h.accountable.id} fallback={h.accountable.name ?? 'someone'} /> is accountable
                </>
              ) : (
                'On a trigger'
              )}
              {` · ${timeAgo(h.createdAt)}`}
            </>
          }
          control={
            <>
              <Button size="sm" isDisabled={busy === h.id} onPress={() => void decide(h, 'approve')}>
                {busy === h.id ? 'Approving…' : 'Approve once'}
              </Button>
              <Button size="sm" variant="ghost" isDisabled={busy === h.id} onPress={() => void decide(h, 'reject')}>
                Reject
              </Button>
            </>
          }
        />
      ))}
      <ErrorNote error={error} />
    </ListGroup>
    ) : null}
    {refused.length > 0 ? (
      <ListGroup id="held-refused" title="Approved, but not done" count={refused.length} description="A person approved these, and the action itself was then refused, so nothing ran. The approval is spent: the agent has to ask again.">
        {refused.map((h) => (
          <ListRow key={h.id} title={h.agent.name ?? 'An agent'} description={`It ${h.words}. ${h.refused?.message ?? 'The action was refused.'} · ${new Date(h.refused?.at ?? h.createdAt).toLocaleString('en-AU')}`} />
        ))}
      </ListGroup>
    ) : null}
    </>
  );
}

/* ---------- who holds what ---------- */

/** Rows shown at a time; "Show more" adds this many. */
const SHOWN = 25;

function Holders({ view, roleName, onAssign, onRemove }: { view: RbacOverview; roleName: (id: string) => string; onAssign?: () => void; onRemove?: (a: RoleAssignment) => void }) {
  const search = useListSearch(150);
  const [role, setRole] = useState('all');
  const [kind, setKind] = useState('all');
  const [shownCount, setShownCount] = useState(SHOWN);
  const q = search.typed.trim().toLowerCase();
  // The roles that are held, so the choice offers only what narrows something.
  const heldRoles = useMemo(() => Object.fromEntries([...new Set(view.assignments.map((a) => a.roleId))].map((id) => [id, roleName(id)])), [view.assignments, roleName]);
  const found = view.assignments.filter((a) => (role === 'all' || a.roleId === role) && (kind === 'all' || a.principal.type === kind) && (!q || [a.principal.name, a.principal.id, roleName(a.roleId), scopeWords(a.scope)].some((t) => t?.toLowerCase().includes(q))));
  const shown = found.slice(0, shownCount);
  const narrowed = q !== '' || role !== 'all' || kind !== 'all';
  const clear = () => {
    search.clear();
    setRole('all');
    setKind('all');
  };
  return (
    <ListGroup
      id="holders"
      title="Who holds what"
      count={listCount(shown.length, { one: 'holder', many: 'holders' }, { total: found.length, hasMore: found.length > shown.length })}
      description="People, groups and agents, the role each holds and where. Roles that come from the directory, a membership or a team’s ownership change there."
      actions={
        <>
          <SearchBox label="Search people, groups and agents" value={search.typed} onChange={(v) => (search.setTyped(v), setShownCount(SHOWN))} placeholder="Search holders" className="w-40 max-w-full @lg:w-52" />
          <ChoiceSelect label="Role" value={role} options={{ all: 'Any role', ...heldRoles }} onChange={(v) => (setRole(v), setShownCount(SHOWN))} className="w-fit min-w-32 max-w-40" />
          <ChoiceSelect label="Kind" value={kind} options={{ all: 'Anyone', user: 'People', group: 'Groups', agent: 'Agents' }} onChange={(v) => (setKind(v), setShownCount(SHOWN))} className="w-fit min-w-28 max-w-full" />
          {onAssign ? (
            <Button size="sm" variant="secondary" onPress={onAssign}>
              Assign
            </Button>
          ) : null}
        </>
      }
      empty={found.length === 0 && narrowed ? <NoMatch tone={TONE} icon={<Persons />} q={search.typed.trim()} filtered={role !== 'all' || kind !== 'all'} noun="holders" onClear={clear} /> : undefined}
    >
      {shown.length === 0 ? <ListBlock className="text-sm text-muted">Nobody holds a role here yet.</ListBlock> : null}
      {shown.map((a) => (
        <ListRow
          key={a.id}
          title={a.principal.name ?? (a.principal.type === 'agent' ? a.principal.id.replace(/^agent:[^/]+\//, '') : <SubjectName id={a.principal.id} />)}
          description={`${a.principal.type === 'user' ? '' : `${PRINCIPAL_TYPE_TEXT[a.principal.type]} · `}${roleName(a.roleId)} · ${scopeWords(a.scope)} · ${a.source === 'default' && a.principal.type === 'agent' ? 'Every agent, in its own team' : HOLDER_SOURCE[a.source]}`}
          compactControl
          control={a.source === 'assigned' && onRemove ? <RowMenu label={`More for ${a.principal.name ?? a.principal.id}`} items={[{ id: 'remove', label: 'Take this role away', danger: true }]} onAction={() => onRemove(a)} /> : undefined}
        />
      ))}
      {found.length > shown.length ? <ShowMore onPress={() => setShownCount((n) => n + SHOWN)} /> : null}
    </ListGroup>
  );
}

/* ---------- how access works ---------- */

const BADGE_ICON: Record<RbacOverview['badges'][number]['key'], ReactElement> = { 'agent-identity': <Fingerprint />, 'secure-runtimes': <Shield />, 'policy-engine': <ListCheck />, 'audit-trail': <FileText /> };

function Principles({ view }: { view: RbacOverview }) {
  return (
    <ListGroup id="how-access-works" title="How access works">
      <ListRow icon={<ShieldCheck />} title="Least privilege" description="Built-in and custom roles, given across the organisation or scoped to one team or project. Nobody gives a role that holds more than they do." />
      <ListRow icon={<HandOk />} title="Humans approve" description="High-impact actions wait for a person to sign off: phone approvals, workflow approvals and policy exceptions. Changing roles needs a recent sign-in." />
      <ListRow icon={<FileText />} title="Every action logged" description="Every role made, changed, given or taken away is written to the audit trail, with who did it and where." />
      <ListBlock>
        <div className="rbac-badges">
          {view.badges.map((b) => (
            <span key={b.key} className="rbac-badge" title={b.detail}>
              <span className="rbac-badge__icon" aria-hidden="true">
                {BADGE_ICON[b.key]}
              </span>
              {b.label}
            </span>
          ))}
        </div>
      </ListBlock>
    </ListGroup>
  );
}

/* ---------- a role, area by area ---------- */

type Table = Record<AreaKey, { all: Verb[]; own: Verb[] }>;

/** A kind's verb in the editor: every area, some areas (or only the holder's own), or none. */
function kindReach(table: Table, kind: ResourceKind, verb: Verb): VerbReach {
  const areas = RESOURCE_AREAS[kind].map((a) => `${kind}.${a}` as AreaKey);
  const full = areas.filter((a) => table[a].all.includes(verb)).length;
  const any = areas.filter((a) => table[a].all.includes(verb) || table[a].own.includes(verb)).length;
  return full === areas.length ? 'all' : any ? 'some' : 'none';
}

/** Words for a kind's partial reach: "Nodes, Mesh nodes and flows; their own Workbenches". */
function partialWords(table: Table, kind: ResourceKind): string | undefined {
  const areas = RESOURCE_AREAS[kind].map((a) => `${kind}.${a}` as AreaKey);
  const own = areas.filter((a) => table[a].own.length);
  const held = areas.filter((a) => table[a].all.length);
  const parts = [held.length && held.length < areas.length ? `on ${listWords(held.map((a) => AREA_TEXT[a]))}` : '', own.length ? `their own ${listWords(own.map((a) => AREA_TEXT[a]))}` : ''].filter(Boolean);
  return parts.length ? parts.join('; ') : undefined;
}

function RoleSheet({ view, role, onClose, onSaved }: { view: RbacOverview; role: RoleDefinition | undefined; onClose: () => void; onSaved: () => void }) {
  const editable = view.editable && (!role || role.source === 'custom' || role.source === 'template');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [grants, setGrants] = useState<string[]>(role ? [...role.grants] : []);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string>();
  const step = useStepUp();
  const table = useMemo(() => grantTable(grants), [grants]);
  const mine = useMemo(() => grantTable([...view.mine, ...NEVER_HELD]), [view.mine]);

  /** Ticking a kind's verb gives it on every area; unticking takes it off every area, their own included. */
  const toggle = (kind: ResourceKind, verb: Verb, on: boolean) => {
    const keep = grants.filter((g) => {
      const m = /^([a-z]+)(?:\.[a-z-]+)?:([a-z]+)(@own)?$/.exec(g);
      return !(m && m[1] === kind && m[2] === verb);
    });
    setGrants(compactGrants(on ? [...keep, `${kind}:${verb}`] : keep));
  };
  const mayTick = (kind: ResourceKind, verb: Verb) => kindReach(mine, kind, verb) === 'all';

  const save = async () => {
    setBusy(true);
    setError(undefined);
    const body = { name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), grants: grants as Grant[] };
    try {
      await step(() => (role ? rbacRoutes.updateRole(role.id, body) : rbacRoutes.createRole(body)), 'Sign in again to change roles.');
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!role) return;
    try {
      await step(() => rbacRoutes.removeRole(role.id), 'Sign in again to remove this role.');
      onSaved();
    } catch (e) {
      setConfirmRemove(false);
      setError(problemText(e));
    }
  };

  const title = role ? role.name : 'New role';
  const intro = !role
    ? 'Tick what it may do on each kind of thing. You can only tick what you may do yourself.'
    : role.source === 'built-in'
      ? `${role.description ?? ''} Built-in roles cannot be changed; make a custom role for anything else.`.trim()
      : role.source === 'today'
        ? `${role.description ?? ''} This is what the platform allows today.`.trim()
        : (role.description ?? 'A custom role.');

  return (
    <>
      <FormSheet
        width="2xl"
        title={title}
        description={intro}
        onClose={onClose}
        footer={
          editable ? (
            <>
              {role ? (
                <Button size="sm" variant="ghost" className="me-auto text-danger" onPress={() => setConfirmRemove(true)}>
                  Remove role
                </Button>
              ) : null}
              <Button size="sm" variant="tertiary" onPress={onClose}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" isPending={busy} isDisabled={!name.trim() || !grants.length} onPress={() => void save()}>
                {role ? 'Save' : 'Make role'}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="tertiary" onPress={onClose}>
              Close
            </Button>
          )
        }
      >
        {editable ? (
          <>
            <FormField label="Name">
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Release manager" maxLength={80} />
            </FormField>
            <FormField label="What it is for" hint="One sentence people see beside the role.">
              <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Approves releases and update rings; changes nothing else." maxLength={400} />
            </FormField>
          </>
        ) : null}
        <table className="grant-editor">
          <thead>
            <tr>
              <th>
                <span className="sr-only">Kind</span>
              </th>
              {VERBS.map((verb) => (
                <th key={verb} title={VERB_TEXT[verb].means}>
                  <span className="hidden sm:inline">{VERB_TEXT[verb].label}</span>
                  <span className="sm:hidden" aria-label={VERB_TEXT[verb].label}>
                    {VERB_TEXT[verb].letter}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {editable
              ? RESOURCE_KINDS.map((kind) => {
                  const partial = partialWords(table, kind);
                  return (
                    <tr key={kind}>
                      <td>
                        <span className="block text-sm font-medium" title={KIND_TEXT[kind].covers}>
                          {KIND_TEXT[kind].label}
                        </span>
                        {partial ? <span className="block text-xs text-muted">Only {partial}</span> : null}
                      </td>
                      {VERBS.map((verb) => {
                        const reach = kindReach(table, kind, verb);
                        const allowed = mayTick(kind, verb);
                        return (
                          <td key={verb}>
                            <Checkbox
                              aria-label={`${KIND_TEXT[kind].label}: ${VERB_TEXT[verb].label}${allowed ? '' : ' (beyond your own)'}`}
                              isSelected={reach === 'all'}
                              isIndeterminate={reach === 'some'}
                              isDisabled={!allowed && reach === 'none'}
                              onChange={(on) => toggle(kind, verb, on && allowed)}
                              className="inline-flex justify-center"
                            >
                              <Checkbox.Content>
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox.Content>
                            </Checkbox>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              : RESOURCE_KINDS.flatMap((kind) => {
                  // Read-only: area by area, the areas it holds anything on; a kind with nothing says so once.
                  const areas = RESOURCE_AREAS[kind].map((x) => `${kind}.${x}` as AreaKey).filter((x) => table[x].all.length || table[x].own.length);
                  const head = (
                    <tr key={kind}>
                      <td colSpan={areas.length ? 8 : 1}>
                        <span className="block text-sm font-medium" title={KIND_TEXT[kind].covers}>
                          {KIND_TEXT[kind].label}
                        </span>
                      </td>
                      {areas.length ? null : (
                        <td colSpan={7} className="text-start text-xs text-muted">
                          Nothing
                        </td>
                      )}
                    </tr>
                  );
                  return [
                    head,
                    ...areas.map((area) => (
                      <tr key={area} className="grant-editor__area">
                        <td>
                          <span className="block ps-3 text-xs">{AREA_TEXT[area]}</span>
                        </td>
                        {VERBS.map((verb) => (
                          <td key={verb}>
                            <VerbMark reach={table[area].all.includes(verb) ? 'all' : table[area].own.includes(verb) ? 'some' : 'none'} verb={verb} />
                          </td>
                        ))}
                      </tr>
                    )),
                  ];
                })}
          </tbody>
        </table>
        <p className="text-xs text-muted">{editable ? 'A filled box holds the verb on everything of that kind; a dash on some of it.' : 'A filled tick holds the verb on everything in that area; an outlined one only on their own (made by them, owned by them or assigned to them).'}</p>
        <dl className="grid gap-1 text-xs text-muted">
          {VERBS.map((v) => (
            <div key={v}>
              <dt className="inline font-medium text-foreground">{VERB_TEXT[v].label}: </dt>
              <dd className="inline">{VERB_TEXT[v].means}</dd>
            </div>
          ))}
        </dl>
        <ErrorNote error={error} />
      </FormSheet>
      <ConfirmDialog isOpen={confirmRemove} title={`Remove ${role?.name ?? 'this role'}?`} confirmLabel="Remove role" danger onConfirm={remove} onCancel={() => setConfirmRemove(false)}>
        Everyone who holds it loses it now. It is kept in the record and in the audit trail.
      </ConfirmDialog>
    </>
  );
}

/* ---------- giving a role ---------- */

function AssignSheet({ view, onClose, onSaved }: { view: RbacOverview; onClose: () => void; onSaved: () => void }) {
  const [type, setType] = useState<PrincipalType>('user');
  const [who, setWho] = useState<{ id: string; name?: string }>();
  const [roleId, setRoleId] = useState<string>();
  const [where, setWhere] = useState<string>('organisation');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const step = useStepUp();
  const mine = useMemo(() => grantTable([...view.mine, ...NEVER_HELD]), [view.mine]);
  // Only roles the assigner may give: within their own; an agent holds at most a Member's, never Manage.
  const roles = view.roles.filter((r) => {
    if (r.source === 'today') return false;
    const t = grantTable(r.grants);
    const within = AREA_KEYS.every((a) => t[a].all.every((v) => mine[a].all.includes(v) || (a === 'data.secrets' && v === 'read')) && t[a].own.every((v) => mine[a].all.includes(v) || mine[a].own.includes(v)));
    if (!within) return false;
    if (type === 'agent') return !r.grants.some((g) => g.includes(':manage') || g.startsWith('roles')) && r.id !== 'platform-administrator';
    return true;
  });
  const role = roles.find((r) => r.id === roleId);
  const scopes: Record<string, string> = {};
  if (!role || role.scopes.includes('organisation')) scopes.organisation = 'Whole organisation';
  if (!role || role.scopes.includes('team')) for (const t of view.teams) scopes[`team:${t.id}`] = `Team: ${t.name}`;
  if (!role || role.scopes.includes('project')) for (const p of view.projects) scopes[`project:${p.id}`] = `Project: ${p.name}`;
  const scope = (): RoleScope => {
    if (where === 'organisation') return { type: 'organisation' };
    const [t, ...rest] = where.split(':');
    return { type: t as 'team' | 'project', id: rest.join(':') };
  };
  const ready = !!who && !!role && scopes[where] !== undefined;

  const save = async () => {
    if (!who || !role) return;
    setBusy(true);
    setError(undefined);
    try {
      await step(() => rbacRoutes.assign({ principal: { type, id: who.id, ...(who.name ? { name: who.name } : {}) }, roleId: role.id, scope: scope() }), 'Sign in again to give someone a role.');
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormSheet
      width="lg"
      title="Assign a role"
      description="Choose who, the role, and where it applies. You can give only roles that hold no more than you do."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!ready} onPress={() => void save()}>
            Assign
          </Button>
        </>
      }
    >
      <FormField label="Who">
        <Segment
          aria-label="A person, a group or an agent"
          size="sm"
          selectedKey={type}
          onSelectionChange={(k) => {
            setType(String(k) as PrincipalType);
            setWho(undefined);
          }}
          className="w-fit"
        >
          {(groupsAvailable() ? (['user', 'group', 'agent'] as const) : (['user', 'agent'] as const)).map((t) => (
            <Segment.Item key={t} id={t}>
              {PRINCIPAL_TYPE_TEXT[t]}
            </Segment.Item>
          ))}
        </Segment>
      </FormField>
      {type === 'agent' ? (
        view.agents.length ? (
          <FormField label="Agent" hint="An agent holds at most a Member’s access, and never decides who may do what.">
            <ChoiceSelect label="Agent" placeholder="Choose an agent" className="w-full" value={who?.id} options={Object.fromEntries(view.agents.map((a) => [a.id, `${a.name} (${a.team})`]))} onChange={(id) => setWho({ id, name: view.agents.find((a) => a.id === id)?.name })} />
          </FormField>
        ) : (
          <p className="text-sm text-muted">No team has a specialist yet, so there is no agent to give a role to.</p>
        )
      ) : who ? (
        <FormField label={type === 'user' ? 'Person' : 'Group'}>
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm">{who.name ?? who.id}</span>
            <Button size="sm" variant="ghost" onPress={() => setWho(undefined)}>
              Change
            </Button>
          </div>
        </FormField>
      ) : (
        <FormField label={type === 'user' ? 'Person' : 'Group'} hint={type === 'group' ? 'A directory group: everyone in it holds the role.' : undefined}>
          <SubjectPicker kinds={[type]} placeholder={type === 'user' ? 'A person’s name' : 'A group’s name'} onPick={(s) => setWho({ id: s.id, ...(s.name ? { name: s.name } : {}) })} />
        </FormField>
      )}
      <FormField label="Role" hint={role ? verbsInWords(role.grants) : undefined}>
        <ChoiceSelect label="Role" placeholder="Choose a role" className="w-full" value={roleId} options={Object.fromEntries(roles.map((r) => [r.id, r.name]))} onChange={(id) => setRoleId(id)} />
      </FormField>
      <FormField label="Where">
        <ChoiceSelect label="Where" className="w-full" value={scopes[where] !== undefined ? where : undefined} placeholder="Choose where" options={scopes} onChange={setWhere} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}
